/**
 * Role gating + role-metadata constants — Phase 3.1.a.2.
 *
 * `hasRole(...names)` and the can* predicates need to read the current
 * user. Rather than passing the user in to every call site, the module
 * holds a thunk that admin.js sets once at boot via `setCurrentUserGetter`.
 * The thunk is re-evaluated on every check, so when `ADMIN_ME` mutates
 * (login flow, role refresh) the predicates see the new value.
 */

let getCurrentUser = () => ({ roles: [] });

/**
 * Wire the current-user lookup. admin.js calls this once at boot and
 * passes a thunk that returns the live `ADMIN_ME` object.
 */
export function setCurrentUserGetter(fn) {
  if (typeof fn === 'function') getCurrentUser = fn;
}

/**
 * Read the current admin user (the live `ADMIN_ME` object). Returns
 * the empty `{ roles: [] }` default before admin.js wires the getter.
 */
export function getCurrentAdminUser() {
  return getCurrentUser();
}

export function hasRole(...names) {
  const user = getCurrentUser();
  const rawRoles = Array.isArray(user?.roles) ? user.roles : [];
  const set = new Set(rawRoles.map((r) => {
    if (r && typeof r === 'object' && r.name) return String(r.name).toLowerCase();
    return String(r).toLowerCase();
  }));
  return names.some((n) => set.has(String(n).toLowerCase()));
}

export function canAccessTicketing() {
  return hasRole(
    'root',
    'customer_care',
    'customer_care_supervisor',
    'field_tech',
    'field_tech_supervisor',
    'system_admin',
    'hotspot_admin',
    'billing_officer',
    'billing_supervisor',
    'sales_officer',
    'sales_supervisor',
  );
}

export function canCreateTickets() {
  return hasRole(
    'root',
    'customer_care',
    'customer_care_supervisor',
    'field_tech_supervisor',
    'billing_officer',
    'billing_supervisor',
  );
}

export function hasCustomerBoardAccess() {
  return hasRole('root', 'customer_care_supervisor');
}

/**
 * Whether the current user may perform edit/mutation actions on a ticket
 * (status, priority, assignment, escalation, inline field edits, etc.).
 *
 * Mirrors the backend `_TICKET_WRITE_ROLES` gate: a `sales_officer` is
 * read-only across ticketing and is intentionally excluded here. Sales
 * officers may still add and edit their own internal comments — that is
 * handled separately by the comment composer, not by this predicate.
 */
export function canEditTickets() {
  return hasRole(
    'root',
    'customer_care',
    'customer_care_supervisor',
    'field_tech',
    'field_tech_supervisor',
    'system_admin',
    'hotspot_admin',
    'billing_officer',
    'billing_supervisor',
    'sales_supervisor',
  );
}

// ----- Role-metadata constants used by the Users view --------------------

export const ROLE_LABELS = {
  root: 'Root administrator',
  system_admin: 'System administrator',
  hotspot_admin: 'Hotspot administrator',
  hr_admin: 'HR administrator',
  hr_manager: 'HR manager',
  customer_care: 'Customer care agent',
  customer_care_supervisor: 'Customer care supervisor',
  field_tech: 'Field technician',
  field_tech_supervisor: 'Field technician supervisor',
  billing_officer: 'Billing officer',
  billing_supervisor: 'Billing supervisor',
  sales_officer: 'Sales officer',
  sales_supervisor: 'Sales supervisor',
  installation_admin: 'Installation admin',
  storekeeper: 'Storekeeper',
  inventory_supervisor: 'Inventory supervisor',
  inventory_finance: 'Inventory finance',
  support_tech: 'Support technician',
  infra_tech: 'Infrastructure technician',
  infra_tech_supervisor: 'Infra supervisor',
  infra_manager: 'Infra manager',
};

export const ROLE_HELP_TEXT = {
  root: 'Full, unrestricted access to every module and setting.',
  system_admin: 'Manages admin users, roles and system jobs.',
  hotspot_admin: 'Manages access points, hotspot distributors and campaigns.',
  hr_admin: 'Full HR admin rights across people, performance, talent, learning, finance.',
  hr_manager: 'HR manager scope for HR modules; use for HR leaders.',
  customer_care: 'Handles customer-facing tickets and support intake.',
  customer_care_supervisor: 'Oversees customer care queue, approvals, and escalations.',
  field_tech: 'Provides on-site support via the technician mobile app.',
  field_tech_supervisor: 'Coordinates field technicians and escalated site visits.',
  billing_officer: 'Resolves billing enquiries and payment follow-ups.',
  billing_supervisor: 'Approves billing adjustments and high-sensitivity escalations.',
  sales_officer: 'Owns sales funnel / revenue-related tickets.',
  sales_supervisor: 'Oversees sales escalations and key opportunities.',
  installation_admin: 'Oversees installation operations — CRM installs, revenue compliance and install reports.',
  storekeeper: 'Receives, issues, transfers and assigns day-to-day stock.',
  inventory_supervisor: 'Stock control — adjustments, write-offs, disposals, catalogue, locations, procurement and installation equipment.',
  inventory_finance: 'Approves gated stock movements and owns inventory valuation.',
  support_tech: 'Handles customer and consumable stock (support technician class).',
  infra_tech: 'Handles infrastructure and tool stock (infra technician class).',
  infra_tech_supervisor: 'Assigns work orders on a team-scoped board, raises stock requests, receives NOC handoffs; also works WOs.',
  infra_manager: 'Owns work-set taxonomy CRUD and staffing; infra-ops oversight.',
};

export const ROLE_DISPLAY_ORDER = Object.keys(ROLE_LABELS);
